import { bannedWords } from "@/common/blacklist";
import {
	PAGES,
	UNSCRAMBLER_MAX_LENGTH,
	UNSCRAMBLER_MAX_WILDCARDS,
} from "@/common/consts";

/**
 * Return given tiles in alphabetical order
 * @param {String} tiles - A String of previously sanitized tiles
 * @return {String} Alphabetical ordered tiles
 */
export const alphabetizeTiles = (tiles, routeName) => {
	// If the site is Spanish, use a Spanish-language ES6 i18n API Collator.
	return [...tiles].sort().join("");
};

/**
 * Sanitize tiles and normalize wildcards
 * @param {String} tiles - User input
 * @param {String} routeName - The name of the route. Needed since some routes have specific allowed characters.
 * @return {String} sanitized tiles
 */
export const sanitizeTiles = (
	tiles,
	routeName,
	isUrl = false,
	allowExtraWildcards = false
) => {
	let wildcards = 0;
	let sanitized = "";

	let testRegex = /[a-z_\-]/i;

	// If the tiles contain an instance of the ñ character, it is safe to assume that it hasn't been sanitised yet.
	if (!isUrl && /ñ/gi.test(tiles)) tiles = tiles.toLowerCase();

	for (let tile of [...tiles]) {
		if (tile === "?") tile = "_";

		sanitized +=
			!testRegex.test(tile) ||
			(!isUrl && tile === "-") ||
			(!allowExtraWildcards &&
				(tile === "_" && ++wildcards) > UNSCRAMBLER_MAX_WILDCARDS)
				? ""
				: tile;
		if (sanitized.length === UNSCRAMBLER_MAX_LENGTH) break;
	}

	if (isUrl) return sanitized;
	return sanitized.toLowerCase();
};

/**
 * Check Tiles entered by user are present in our blacklist or not
 *
 * @param {string} tiles The tiles entered by user for search
 * @return {string} true if entered tiles contain a full word that is present in our bannedWords hashmap
 */
export const isTilesInBlacklist = (tiles) => {
	return tiles.split(/[^a-z]/).some((word) => bannedWords[word]);
};

/**
 * Removes the nth occurence of a char in a given String.
 * @param {String} tiles The tiles entered by user
 * @param {String} char The char who we look to remove
 * @param {Number} occurrence The occurence number to remove
 * @returns {String} The given input String with the char removed
 */
export const removeNthChar = (tiles, char, occurrence) => {
	return tiles.replace(
		new RegExp(`^(?:.*?[${char}]){${occurrence}}`),
		(substring) => substring.replace(new RegExp(`[${char}]$`), "")
	);
};

/**
 * Recursively remove a char from a given String until the count of that char is bellow a given threshold.
 *
 * @param {String} tiles The tiles entered by user
 * @param {String} char The char to remove if count is above threshold
 * @param {Number} maxOccurrence Threshold of the max allowed char occurrences.
 * @returns {String} The given input String with less than max char occurrence count.
 */
export const removeCharUntilMax = (tiles, char, maxOccurrence) => {
	if (maxOccurrence === 0) {
		return tiles.replace(new RegExp(`[${char}]`, "g"), "");
	}

	const count = (tiles.match(new RegExp(`[${char}]`, "g")) || []).length;
	if (count <= maxOccurrence) {
		return tiles;
	}

	return removeCharUntilMax(
		removeNthChar(tiles, char, count),
		char,
		maxOccurrence
	);
};

/**
 * Replace non-English Characters
 *
 * @param {String} char The char who we look to remove
 * @returns {String} The given input String with the char removed
 */
export const allowOnlyEnChar = (char) => {
	return char.replace(/[^a-zA-Z_\?]/gi, "");
};

/**
 * Replace non-English Characters and replace space to "?"
 *
 * @param {String} char The char who we look to remove
 * @returns {String} The given input String with the char removed
 */
export const allowOnlyEnCharAndSpace = (char) => {
	return allowOnlyEnChar(char.replace(/\s/, "?"));
};
